import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AlertInfoIcon,
  PlusIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AsyncBox,
  Banner,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  generateAdverseActionLetterOptions,
  generateConditionsForApprovalOptions,
} from '../../../containers/ApplicationProfile/LeaseApplicationTab/CreditCriminalStatus/AdverseActionLetterModal/utils';
import { useCreateAdverseActionLetterModal, toMethodKey } from './hooks';
import { Props } from './types';

// eslint-disable-next-line max-len
import oldComponentMessages from '../../../containers/ApplicationProfile/LeaseApplicationTab/CreditCriminalStatus/AdverseActionLetterModal/messages';
import newMessages from './messages';

const messages = { ...oldComponentMessages, ...newMessages };

const AdverseActionLetterModal = ({
  intl,
  adverseActionDeadlineDays,
  open,
  handleClose,
  promptToaster,
  modalProps,
  screeningLetterTypes,
  applicationId,
  organizationId,
  propertyId,
  screeningData,
}: Props) => {
  const {
    adverseActionLetter,
    adverseActionLetterApprovalConditions,
    leaseAppRevampTransunion,
  } = useFlags();

  const {
    reactHookFormProps,
    isLoading,
    screeningLetterOptions,
    conditionsForApprovalOptions,
    showConditionsForApproval,
    adverseScreeningMethods,
    otherCheckboxSelected,
    selectedValueNeedsAmount,
    allCheckboxesButOtherDeselected,
    otherOptionsSelected,
    saveAdverseActionLetter,
    isSavingAdverseActionLetter,
    conditionsForApprovalAmount,
  } = useCreateAdverseActionLetterModal({
    open,
    promptToaster,
    adverseActionLetterApprovalConditions,
    screeningLetterTypes,
    applicationId,
    organizationId,
    propertyId,
    handleClose,
    screeningData,
  });

  return leaseAppRevampTransunion ? (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Adverse Action Letter"
      actionsProps={[
        {
          variant: 'text',
          children: 'Cancel',
          onClick: handleClose,
        },
        {
          children: 'Create',
          submitButton: true,
          isSubmitting: isSavingAdverseActionLetter,
          disabled:
            isLoading ||
            (allCheckboxesButOtherDeselected && !otherOptionsSelected) ||
            (selectedValueNeedsAmount && !conditionsForApprovalAmount),
          onClick: saveAdverseActionLetter,
        },
      ]}
      icon={<PlusIcon />}
      ContentProps={{
        sx: {
          width: {
            xs: 'calc(100% - 32px)',
            sm: 400,
          },
        },
      }}
      {...modalProps}
    >
      <AsyncBox loading={isLoading}>
        <Form
          spacing={1}
          ReactHookFormProps={reactHookFormProps}
          fields={[
            ...(adverseActionLetter
              ? [
                  {
                    name: 'custom',
                    variant: 'custom',
                    component: (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          color={'text.secondary'}
                          variant="bodySmall"
                        >
                          {intl.formatMessage(messages.expiration, {
                            adverseActionDeadlineDays:
                              adverseActionDeadlineDays ?? 10,
                          })}
                        </Typography>
                        <AlertInfoIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Box>
                    ),
                    GridProps: {
                      xs: 12,
                    },
                  },
                ]
              : []),
            {
              name: 'custom',
              variant: 'custom',
              component: (
                <>
                  <Typography variant={'labelLarge'}>
                    <FormattedMessage {...messages.reasons} />
                  </Typography>
                </>
              ),
              GridProps: {
                xs: 12,
                marginTop: 1,
              },
            },
            ...adverseScreeningMethods.map((m) => ({
              variant: 'checkbox',
              name: toMethodKey(m.name),
              label: m.name,
              GridProps: {
                xs: 12,
              },
            })),
            {
              variant: 'checkbox',
              name: 'other',
              label: 'Other',
              GridProps: {
                xs: 3,
                marginTop: 1,
              },
            },
            {
              variant: 'multiSelect',
              name: 'otherScreeningOptions',
              GridProps: { xs: 9 },
              options: generateAdverseActionLetterOptions(
                screeningLetterOptions,
                intl.locale,
              ).map((option) => ({
                value: option.value,
                text: option.label,
              })),
              disabled: !otherCheckboxSelected,
              helperText: '',
            },
            ...(showConditionsForApproval
              ? [
                  {
                    name: 'custom',
                    variant: 'custom',
                    component: (
                      <>
                        <Typography variant={'labelLarge'}>
                          <FormattedMessage {...messages.conditions} />
                        </Typography>
                      </>
                    ),
                    GridProps: {
                      xs: 12,
                      marginTop: 1,
                    },
                  },
                  {
                    variant: 'singleSelect',
                    name: 'conditionsForApproval',
                    options: generateConditionsForApprovalOptions(
                      conditionsForApprovalOptions,
                      intl.locale,
                    ),
                    GridProps: { xs: 12 },
                    helperText: '',
                  },

                  {
                    variant: 'number',
                    name: 'conditionsForApprovalAmount',
                    rules: {
                      required: selectedValueNeedsAmount,
                      min: 0.01,
                      step: 0.01,
                    },
                    disabled: !selectedValueNeedsAmount,
                    adornment: '$',
                    placeholder: '0.00',
                    GridProps: {
                      xs: 6,
                    },
                    helperText: '',
                  },
                ]
              : []),
            {
              variant: 'custom',
              name: 'custom',
              component: (
                <>
                  <Banner
                    text={intl.formatMessage(messages.noteMessage)}
                    TypographyProps={{
                      variant: 'bodySmall',
                      sx: { textWrap: 'wrap' },
                    }}
                    color="lightGrey"
                    hasBorder={false}
                    icon={<AlertInfoIcon />}
                  />
                </>
              ),
              GridProps: {
                xs: 12,
                marginTop: 1,
              },
            },
          ]}
        />
      </AsyncBox>
    </Modal>
  ) : null;
};

export default AdverseActionLetterModal;
